import { BaseImage } from "@components/BaseImage";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { ISbStoryData, SbBlokData } from "@storyblok/react";
import { Link } from "react-router-dom";
import type { PreservedSearchState } from "@hooks/usePreservedSearchQuery";
import { twMerge } from "tailwind-merge";
import { NewsMetaData } from "./NewsMetaData";
import { HoverArrow } from "../HoverArrow";
import { LeadershipLabel } from "./LeadershipLabel";
import { getDescriptionFromBody } from "@utils/getDescriptionFromBody";

interface NewsItemProps {
  title: string;
  slug: string;
  date: string;
  introduction?: string | null;
  body?: SbBlokData[];
  imageUrl: string;
  imageAlt: string;
  categories?: ISbStoryData[];
  brand?: ISbStoryData[];
  isHorizontal?: boolean;
  isLeadership?: boolean;
  className?: string;
  linkState?: PreservedSearchState;
}
export const NewsItem = ({
  title,
  slug,
  date,
  introduction,
  body,
  imageUrl,
  imageAlt,
  categories,
  brand,
  isHorizontal = false,
  isLeadership = false,
  className,
  linkState,
}: NewsItemProps) => {
  const description = introduction?.trim()
    ? introduction
    : getDescriptionFromBody(body ?? []);

  return (
    <div
      className={twMerge(
        "group relative block h-full",
        isHorizontal ? "flex items-center md:block" : "",
        className
      )}
    >
      <div className="relative flex-shrink-0">
        <BaseImage
          imageUrl={imageUrl}
          alt={imageAlt}
          className={`${isHorizontal ? "h-25 w-32" : "h-32.5 w-full"} object-cover md:h-64 md:w-full`}
        />
        {isLeadership && <LeadershipLabel className="top-2" />}
        <HoverArrow isHiddenSmallScreen className="absolute right-4 bottom-4" />
      </div>
      <div
        className={`flex flex-grow flex-col gap-2 md:px-2 md:py-3 ${isHorizontal ? "p-3" : "p-1"}`}
      >
        <div className="flex flex-col gap-1">
          <NewsMetaData categories={categories} date={date} brand={brand} />
          <Typography
            size="sm"
            className="leading-[120%] font-semibold text-secondary-800 md:text-xl dark:text-white"
          >
            <Link to={slug} state={linkState}>
              {/* Make sure the whole card is clickable */}
              <span className="absolute inset-0"></span>
              {title}
            </Link>
          </Typography>
        </div>
        {description && (
          <Typography className="hidden font-normal text-tertiary-800 md:line-clamp-4 dark:text-tertiary-200">
            {description}
          </Typography>
        )}
      </div>
    </div>
  );
};
